import { Button, DatePicker, Form, Input, Table, Typography } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import VSpace from '../../common/VSpace'
import { useChatSearch, useExportChatsMutation } from './chatSearchQueries'
import {
  ChatSearchParams,
  ChatSearchResultItem,
  formatClient,
  getDefaultSearchParams,
} from './chatSearchModel'
import ChatSessionModal from './ChatSessionModal'

const { RangePicker } = DatePicker

type FormValues = {
  q?: string
  userName?: string
  sessionId?: string
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs]
}

const ChatSearchPanel = () => {
  const [form] = Form.useForm<FormValues>()
  const [searchParams, setSearchParams] = useState<ChatSearchParams | null>(() => getDefaultSearchParams())
  const defaultDateRange = useMemo<[dayjs.Dayjs, dayjs.Dayjs]>(() => {
    const params = getDefaultSearchParams()
    return [dayjs(params.from), dayjs(params.to)]
  }, [])
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const { data, isLoading } = useChatSearch(searchParams)
  const { mutate: runExport, isLoading: exporting } = useExportChatsMutation()

  const handleSearch = () => {
    const values = form.getFieldsValue()
    setSearchParams({
      q: values.q || undefined,
      userName: values.userName || undefined,
      sessionId: values.sessionId || undefined,
      from: values.dateRange?.[0]?.startOf('day').toISOString(),
      to: values.dateRange?.[1]?.endOf('day').toISOString(),
      page: 1,
      pageSize: searchParams?.pageSize ?? 20,
    })
  }

  const columns = [
    {
      title: 'Session',
      dataIndex: 'session_id',
      width: 160,
      render: (val: string) => (
        <Typography.Text
          copyable={{ text: val, tooltips: ['Copy session ID', 'Copied!'] }}
          ellipsis
          style={{ maxWidth: 120, fontSize: 'inherit' }}
          onClick={(e) => e.stopPropagation()}
        >
          {val}
        </Typography.Text>
      ),
    },
    {
      title: 'User',
      dataIndex: 'user_name',
      sorter: (a: ChatSearchResultItem, b: ChatSearchResultItem) =>
        a.user_name.localeCompare(b.user_name),
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      sorter: (a: ChatSearchResultItem, b: ChatSearchResultItem) =>
        a.started_at.localeCompare(b.started_at),
      render: (val: string) => dayjs(val).format('MMM D, YYYY h:mm A'),
    },
    {
      title: 'Last Message',
      dataIndex: 'last_message_at',
      render: (val: string) => dayjs(val).format('MMM D, YYYY h:mm A'),
    },
    {
      title: 'Client',
      dataIndex: 'client',
      // No sorter, matching the Model column: both are nullable low-cardinality
      // strings where antd would only sort the current page anyway.
      render: (val: string | null) => formatClient(val),
    },
    {
      title: 'Messages',
      dataIndex: 'message_count',
      sorter: (a: ChatSearchResultItem, b: ChatSearchResultItem) =>
        a.message_count - b.message_count,
    },
    {
      title: 'Model',
      dataIndex: 'model',
      render: (val: string | null) => val?.replace('anthropic/', '') ?? '—',
    },
    {
      title: 'Tokens',
      key: 'tokens',
      render: (_: any, record: ChatSearchResultItem) =>
        `${record.input_tokens.toLocaleString()} / ${record.output_tokens.toLocaleString()}`,
    },
    {
      title: 'Cost',
      dataIndex: 'cost_usd',
      sorter: (a: ChatSearchResultItem, b: ChatSearchResultItem) =>
        parseFloat(a.cost_usd) - parseFloat(b.cost_usd),
      render: (val: string) => `$${parseFloat(val).toFixed(4)}`,
    },
  ]

  return (
    <VSpace>
      <Form form={form} onFinish={handleSearch}>
        <Form.Item name="q" style={{ marginBottom: 8 }}>
          <Input placeholder="Search message content" allowClear />
        </Form.Item>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Form.Item name="userName" style={{ marginBottom: 0 }}>
            <Input placeholder="User name" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="sessionId" style={{ marginBottom: 0 }}>
            <Input placeholder="Session ID" allowClear style={{ width: 300 }} />
          </Form.Item>
          <Form.Item name="dateRange" initialValue={defaultDateRange} style={{ marginBottom: 0 }}>
            <RangePicker />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">Search</Button>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              icon={<DownloadOutlined />}
              disabled={!data?.total}
              loading={exporting}
              onClick={() => searchParams && runExport(searchParams)}
            >
              Download
            </Button>
          </Form.Item>
        </div>
      </Form>
      <Table
        dataSource={data?.results}
        loading={isLoading}
        rowKey="session_id"
        columns={columns}
        onRow={(record) => ({
          onClick: () => setSelectedSessionId(record.session_id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: searchParams?.page ?? 1,
          pageSize: searchParams?.pageSize ?? 20,
          total: data?.total,
          showSizeChanger: true,
          showTotal: (total) => `${total} sessions`,
          onChange: (page, pageSize) =>
            setSearchParams((prev) => prev ? { ...prev, page, pageSize } : null),
        }}
      />
      <ChatSessionModal
        sessionId={selectedSessionId}
        onClose={() => setSelectedSessionId(null)}
      />
    </VSpace>
  )
}

export default ChatSearchPanel
